/**
 * The numbered loader's motion, worked out ahead of time.
 *
 * The counter used to move a React state on every animation frame. The
 * page mounting behind the curtain (a WebGL scene, say) stalls those
 * frames, so the digits froze and then jumped. Now the loader bakes where
 * the value will be over the next while, as evenly spaced samples, and
 * NumberedLoader hands each wheel's positions to the compositor as Web
 * Animations keyframes: the digits keep rolling while the main thread is
 * busy. When the target moves, the trajectory is rebuilt from the state
 * the value is in at that moment, so nothing snaps.
 *
 * Pure (no DOM), unit-tested in counterMotion.test.ts.
 */

export interface Trajectory {
    /** Timeline time (ms) of sample 0. */
    startMs: number;
    /** Spacing between samples (ms). */
    dtMs: number;
    /** The value shown, one per sample. At least two. */
    values: number[];
    /** The follower's inner stage per sample (see `follow`), carried so a
     *  later follow continues at the same speed. Equals `values` for a
     *  sprint. */
    inner: number[];
}

/** Where a follower is: its inner stage, the value it shows and how
 *  fast that value is moving (units per ms). */
export interface FollowerState {
    inner: number;
    value: number;
    speed: number;
}

export const AT_REST: FollowerState = { inner: 0, value: 0, speed: 0 };

/** Total play time of a trajectory (ms). */
export const trajectoryDurationMs = (traj: Trajectory) => (traj.values.length - 1) * traj.dtMs;

/** The follower's state at timeline time `tMs`: interpolated between
 *  samples, held at either end (speed 0 there). */
export function stateAt(traj: Trajectory, tMs: number): FollowerState {
    const last = traj.values.length - 1;
    const raw = (tMs - traj.startMs) / traj.dtMs;
    const x = Math.max(0, Math.min(last, raw));
    const i = Math.min(last - 1, Math.floor(x));
    const f = x - i;
    const lerp = (arr: number[]) => (last === 0 ? arr[0] : arr[i] + (arr[i + 1] - arr[i]) * f);
    const speed = last === 0 || raw < 0 || raw > last ? 0 : (traj.values[i + 1] - traj.values[i]) / traj.dtMs;
    return { inner: lerp(traj.inner), value: lerp(traj.values), speed };
}

/**
 * A follower chasing `input(t)` from `state`: two first-order lags of
 * `tauMs` in cascade, the second never faster than `maxUnitsPerMs`. One
 * lag would set off at full speed the instant the target moves and slow
 * from there, a jolt per progress event (the strip streams in chunks);
 * the second lag smooths that speed, so the digits swell and ease
 * instead. The speed cap turns a flood of bytes into a steady climb
 * rather than a blur: a lag's speed scales with the gap, and a fast
 * connection opens a gap of seventy units at once. The acceleration cap
 * (`maxAccelPerMs2`) is how the climb gets going: a big gap would
 * otherwise take the lags from a crawl to the speed cap within two
 * samples. A lag never overshoots a rising input and the caps only slow
 * it, so progress never rolls back and the speed stays continuous. Runs
 * until the value has settled on the input (within `settleEps`) or
 * `maxMs`, whichever is first.
 */
export function follow(
    state: FollowerState,
    input: (tMs: number) => number,
    tauMs: number,
    dtMs: number,
    maxMs: number,
    maxUnitsPerMs = Infinity,
    maxAccelPerMs2 = Infinity,
    settleEps = 0.01,
): Pick<Trajectory, "values" | "inner"> {
    const k = 1 - Math.exp(-dtMs / tauMs);
    const maxStep = maxUnitsPerMs * dtMs;
    const maxStepGain = maxAccelPerMs2 * dtMs * dtMs;
    let y1 = state.inner;
    let y2 = state.value;
    let step = Math.max(0, state.speed) * dtMs;
    const values = [y2];
    const inner = [y1];
    const n = Math.max(1, Math.ceil(maxMs / dtMs));
    for (let i = 1; i <= n; i++) {
        const u = input(i * dtMs);
        y1 += (u - y1) * k;
        step = Math.min(maxStep, step + maxStepGain, (y1 - y2) * k);
        y2 += step;
        values.push(y2);
        inner.push(y1);
        if (Math.abs(u - y2) < settleEps && Math.abs(y1 - y2) < settleEps) break;
    }
    return { values, inner };
}

export interface SprintTiming {
    /** Average speed the run is paced at (units per second)... */
    unitsPerS: number;
    /** ...within these bounds (ms): a short hop is not over in a blink,
     *  a long run does not drag. */
    minMs: number;
    maxMs: number;
}

/**
 * The run from `from`, moving at `speed` (units per ms), to exactly `to`
 * at a standstill: the finish once the page is ready. A cubic Hermite
 * from the current speed to zero, so the digits never kick off or stop
 * dead: from rest it is an ease-in-out, mid-roll it carries the speed it
 * has. The length is paced by the distance (`SprintTiming`), unless
 * `landInMs` sets it (the page transition's counter lands as the logo
 * comes to rest), and is never under `minMs` either way, so a late word
 * cannot make the digits dash. The start tangent is held within three
 * times the distance, the bound under which such a cubic cannot
 * overshoot. The last sample is `to` itself, so the wheels stop on the
 * digit.
 */
export function sprint(
    from: number,
    speed: number,
    to: number,
    dtMs: number,
    timing: SprintTiming,
    landInMs?: number,
): number[] {
    const d = to - from;
    const paced = Math.min(timing.maxMs, (Math.abs(d) / timing.unitsPerS) * 1000);
    const durationMs = Math.max(timing.minMs, landInMs ?? paced);
    const n = Math.max(1, Math.ceil(durationMs / dtMs));
    const m0 = Math.max(0, Math.min(3 * d, speed * n * dtMs));
    const values: number[] = [];
    for (let i = 0; i < n; i++) {
        const u = i / n;
        const u2 = u * u;
        const u3 = u2 * u;
        values.push((2 * u3 - 3 * u2 + 1) * from + (u3 - 2 * u2 + u) * m0 + (-2 * u3 + 3 * u2) * to);
    }
    values.push(to);
    return values;
}

/**
 * The pace the entrance's counter sets itself when loading runs ahead of
 * the ring: a straight climb from `v0` (its value at `startMs`) to 100,
 * timed so that the finishing run (`sprint`) lands exactly at `landAtMs`.
 * The follower trails a ramp by about two lags' worth of travel, and the
 * finish covers that trail in at least `timing.minMs`, so the climb ends
 * that long before `landAtMs`. Returns the input and the time it reaches
 * 100 (when the finish must start).
 */
export function paceCurve(
    v0: number,
    startMs: number,
    landAtMs: number,
    lagMs: number,
    timing: SprintTiming,
): { at: (tMs: number) => number; endMs: number } {
    const span = Math.max(1, landAtMs - startMs - timing.minMs);
    const rate = (100 - v0) / span;
    const trail = 2 * lagMs * rate;
    const finishMs = Math.max(timing.minMs, (trail / timing.unitsPerS) * 1000);
    const endMs = Math.max(startMs + 1, landAtMs - finishMs);
    const at = (tMs: number) => {
        const u = (tMs - startMs) / (endMs - startMs);
        return u <= 0 ? v0 : u >= 1 ? 100 : v0 + (100 - v0) * u;
    };
    return { at, endMs };
}
